import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight, BadgeCheck } from "lucide-react";

export function ProofsSection() {
  const [proofs, setProofs] = useState([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    api.get("/proofs").then((r) => setProofs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (proofs.length < 2) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % proofs.length), 4500);
    return () => clearInterval(t);
  }, [proofs.length]);

  if (proofs.length === 0) return null;

  const prev = () => setIndex((i) => (i - 1 + proofs.length) % proofs.length);
  const next = () => setIndex((i) => (i + 1) % proofs.length);

  return (
    <section className="py-20 px-4 bg-primary/5" data-testid="proofs-section">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-secondary font-medium mb-3">
            <BadgeCheck className="w-5 h-5" />
            <span className="uppercase tracking-widest text-sm">Trusted &amp; Verified</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-primary">
            Proof of Happy <span className="text-secondary">Journeys</span>
          </h2>
        </div>

        <div className="relative">
          <div className="overflow-hidden rounded-3xl shadow-xl">
            <div
              className="flex transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
              data-testid="proofs-track"
            >
              {proofs.map((p) => (
                <div key={p.proof_id} className="w-full shrink-0" data-testid={`proof-slide-${p.proof_id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 bg-white">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-72 md:h-96 object-cover"
                    />
                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <h3 className="font-serif text-2xl font-bold text-primary mb-3">{p.title}</h3>
                      {p.text && (
                        <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{p.text}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {proofs.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous proof"
                data-testid="proofs-prev-btn"
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center text-primary hover:bg-white transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                aria-label="Next proof"
                data-testid="proofs-next-btn"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center text-primary hover:bg-white transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="flex justify-center gap-2 mt-6">
                {proofs.map((p, i) => (
                  <button
                    key={p.proof_id}
                    onClick={() => setIndex(i)}
                    aria-label={`Go to proof ${i + 1}`}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      i === index ? "w-8 bg-secondary" : "w-2.5 bg-primary/20 hover:bg-primary/40"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
